import { PaymentProviderCapability, PaymentGateway, PaymentsAccount } from '../domain/PaymentsAccount'
import {
    Appearance,
    Stripe,
    StripeElements,
    loadStripe,
} from '@stripe/stripe-js'
import {
    createApplePay,
    createCheckout,
    createController,
    createGooglePay,
    RyftApplePayComponentResponse,
    type RyftBeforePaymentAttemptResponse,
    RyftPaymentAttemptFinalResponse,
    type RyftPaymentAttemptResponse,
    type RyftCheckoutComponentResponse,
    type RyftGooglePayComponentResponse,
    type RyftUsage,
} from '@ryftpay/web'
import { BookingDetails } from '../domain/BookingDetails'
import { BookingRequest } from '../domain/BookingRequest'
import { DTOAdapter } from './DTOs/DTOAdapter'
import { PaymentIntentDTO } from './DTOs/PaymentIntentDTO'
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http'
import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { PaymentIntent } from '../domain/PaymentIntent'
import {
    PaymentSession,
    PaymentSessionOptions,
    PaymentSessionValidationError,
} from './PaymentSession'
import { environment } from '../../environments/environment'
import { firstValueFrom, map, catchError, throwError } from 'rxjs'
import { Sale } from '../domain/Sale'
import { SaleDTO } from './DTOs/SaleDTO'
import { Venue } from '../domain/Venue'
import { DeliveryDetails } from '../domain/DeliveryDetails'
import { DojoClientLoader } from './dojo/dojo-client-loader.service'
import { DojoSession } from './dojo/dojo-session'
import { TeyaClientLoader } from './teya/teya-client-loader.service'
import { TeyaSession } from './teya/teya-session'
import { WindowService } from './window/window.service'
import { BookingService } from './booking.service'

export type SaleDeliveryMethod = 'EMAIL' | 'POSTAL'

export interface CustomerPaymentRequirement {
    cardDetailsNotRequired: boolean
    depositAmount: number | null
    depositAmountIsPerCover: boolean | null
}

export interface PaymentRequirementContact {
    firstName: string
    lastName: string | null
    emailAddress: string
    phoneNumber: string | null
}

@Injectable({
    providedIn: 'root',
})
export class PaymentsService {

    private stripeByAccountId = new Map<string, Promise<Stripe>>()

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
        private bookingService: BookingService,
        private dojoClientLoader: DojoClientLoader,
        private teyaClientLoader: TeyaClientLoader,
        private windowService: WindowService,
        @Inject(DOCUMENT) private readonly document: Document
    ) { }

    async getCustomerPaymentRequirement(
        venueId: string,
        contact: PaymentRequirementContact
    ): Promise<CustomerPaymentRequirement> {
        const url = `${environment.apiBaseUrl}/booking/venue/${venueId}/payment-requirement`
        const requirement = this.http.post<CustomerPaymentRequirement>(url, contact).pipe(
            catchError((error: HttpErrorResponse) => throwError(() => error))
        )
        return firstValueFrom(requirement)
    }

    async makeSetupIntentClientSecret(
        organisationId: string,
        businessId: string,
        venueId: string,
        areaId: string,
        bookingRequest: BookingRequest
    ): Promise<PaymentIntent | null> {
        let params = new HttpParams()
            .set('venueId', venueId)
            .set('areaId', areaId)
        const url = `
${environment.apiBaseUrl}\
/booking\
/organisation/${organisationId}\
/business/${businessId}\
/payments\
/setup-details\
?${params.toString()}`
        const payment = this.http.post<PaymentIntentDTO>(
            url,
            this.dtoAdapter.adaptBookingRequest(bookingRequest),
            { observe: 'response' }
        ).pipe(
            map(response => {
                if (response.status === 204) {
                    return null
                }
                return response.body ? this.adaptPaymentIntent(response.body) : null
            }),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error)
            })
        )
        return firstValueFrom(payment)
    }

    async makePendingPaymentSetupIntentClientSecret(
        organisationId: string,
        businessId: string,
        bookingDetails: BookingDetails
    ): Promise<PaymentIntent> {
        let params = new HttpParams()
            .set('bookingId', bookingDetails.bookingId)
        const url = `
${environment.apiBaseUrl}\
/booking\
/organisation/${organisationId}\
/business/${businessId}\
/payments\
/setup-details\
?${params.toString()}`
        const payment = this.http.post<PaymentIntent>(url, null).pipe(
            map(intent => new PaymentIntent(intent.id, intent.clientSecret, bookingDetails, intent.confirmationPending)),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error)
            })
        )
        return firstValueFrom(payment)
    }

    async makePaymentIntentClientSecret(
        organisationId: string,
        businessId: string,
        venueId: string,
        areaId: string,
        bookingRequest: BookingRequest
    ): Promise<PaymentIntent | null> {
        let params = new HttpParams()
            .set('venueId', venueId)
            .set('areaId', areaId)
        const url = `
${environment.apiBaseUrl}\
/booking\
/organisation/${organisationId}\
/business/${businessId}\
/payments\
/payment-intent\
?${params.toString()}`
        const body = this.dtoAdapter.adaptBookingRequest(bookingRequest)
        const payment = this.http.post<PaymentIntentDTO | null>(
            url,
            body
        ).pipe(
            map(intent => intent === null ? null : this.adaptPaymentIntent(intent)),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error)
            })
        )
        return firstValueFrom(payment)
    }

    async makePendingBookingPaymentIntentClientSecret(
        organisationId: string,
        businessId: string,
        bookingDetails: BookingDetails
    ): Promise<PaymentIntent> {
        let params = new HttpParams()
            .set('bookingId', bookingDetails.bookingId)
        const url = `
${environment.apiBaseUrl}\
/booking\
/organisation/${organisationId}\
/business/${businessId}\
/payments\
/payment-intent\
?${params.toString()}`
        const payment = this.http.post<PaymentIntent>(url, null).pipe(
            map(intent => new PaymentIntent(intent.id, intent.clientSecret, bookingDetails, intent.confirmationPending)),
            catchError((error: HttpErrorResponse) => {
                return throwError(() => error)
            })
        )
        return firstValueFrom(payment)
    }

    async confirmSetupIntent(
        organisationId: string,
        businessId: string,
        setupIntentId: string,
        booking: { bookingRequestId: string } | { bookingId: string }
    ): Promise<boolean> {
        const params = new HttpParams({ fromObject: booking })
        const url = `${environment.apiBaseUrl}/booking/organisation/${organisationId}`
            + `/business/${businessId}/payments/setup-details/`
            + `${encodeURIComponent(setupIntentId)}/confirm?${params.toString()}`
        const response = await firstValueFrom(this.http.post<void>(url, null, {
            observe: 'response',
        }))
        return response.status === 204
    }

    async makeSalePaymentIntent(
        venue: Venue,
        productId: string,
        skuId: string,
        quantity: number,
        deliveryMethod: SaleDeliveryMethod = 'EMAIL',
    ): Promise<{ sale: Sale, paymentIntent: PaymentIntent }> {
        if (!venue.paymentsAccountForCapability('VoucherPayments')?.id) {
            throw new Error('Voucher payments are not supported by this payment provider.')
        }
        const url = `${environment.apiBaseUrl}/sale/venue/${venue.id}`
        const body = {
            deliveryMethod,
            items: [
                {
                    productId: productId,
                    skuId: skuId,
                    quantity: quantity,
                },
            ],
        }
        const payment = this.http.post<any>(url, body)
            .pipe(
                map((response) => {
                    const sale = this.dtoAdapter.adaptSaleDto(response.first as SaleDTO)
                    const paymentIntent = response.second as PaymentIntent
                    return { sale: sale, paymentIntent: paymentIntent }
                })
            )
        return firstValueFrom(payment)
    }

    async setSaleFulfilmentDetails(
        venueId: string,
        saleId: string,
        deliveryDetails: DeliveryDetails,
        senderMessage?: string | null
    ): Promise<Sale> {
        const url = `${environment.apiBaseUrl}
/sale\
/venue/${venueId}\
/fulfilment`
        const body = {
            saleId: saleId,
            deliveryDetails: {
                emailAddress: deliveryDetails.emailAddress,
                name: deliveryDetails.name,
                postalDeliveryAddress: deliveryDetails.postalDeliveryAddress,
            },
            message: senderMessage
        }
        const response = this.http.post<SaleDTO>(url, body)
            .pipe(map((dto) => this.dtoAdapter.adaptSaleDto(dto)))
        return firstValueFrom(response)
    }

    /**
     * Create a gateway-agnostic payment session for the given venue and intent.
     *
     * The returned session encapsulates all gateway-specific behaviour. Callers
     * subscribe to validity / wallet / error callbacks and call `confirm()` when
     * the user presses the "Pay" button.
     */
    async createPaymentSession(
        venue: Venue,
        options: PaymentSessionOptions,
        capability: PaymentProviderCapability = options.usage === 'setup-card' ? 'CancellationChargePayments' : 'DepositPayments'
    ): Promise<PaymentSession> {
        const account = venue.paymentsAccountForCapability(capability)
        if (!account?.id) {
            throw new Error('Payments account not configured for this venue')
        }
        const refreshPaymentIntent = (paymentIntentId = options.intent.id): Promise<PaymentIntent> => {
            const url = `${environment.apiBaseUrl}/booking/organisation/${encodeURIComponent(venue.organisationId)}`
                + `/business/${encodeURIComponent(venue.businessId)}/payments/payment-intent/${encodeURIComponent(paymentIntentId)}/refresh`
            return firstValueFrom(this.http.post<PaymentIntent>(url, null, {
                params: new HttpParams().set('paymentsAccountId', account.id),
            }))
        }
        const gateway = account.gateway
        switch (gateway) {
        case 'tablesense-test': {
            if (environment.production) throw new Error('TableSense Test payments are unavailable in production')
            return new TableSenseTestSession(this.document, options, refreshPaymentIntent)
        }
        case 'ryftpay':
            return new RyftSession(account, venue, options)
        case 'teya': {
            const Client = await this.teyaClientLoader.load()
            return TeyaSession.create(Client, options, emailAddress => this.updateBookingEmailForPayment(venue, options.intent.bookingDetails, emailAddress))
        }
        case 'dojo': {
            if (options.usage === 'setup-card') {
                return new DojoHostedCheckoutSession(options, this.windowService)
            }
            const dojo = await this.dojoClientLoader.load()
            return DojoSession.create(dojo, this.document, options, refreshPaymentIntent)
        }
        case 'stripe':
        case 'breathepay': {
            const stripe = await this.loadStripe(account.id)
            return new StripeSession(gateway, stripe, options)
        }
        default:
            return unsupportedPaymentGateway(gateway)
        }
    }

    private adaptPaymentIntent(intent: PaymentIntentDTO): PaymentIntent {
        return new PaymentIntent(intent.id, intent.clientSecret,
            intent.bookingDetails ? this.dtoAdapter.adaptBookingDetailsDto(intent.bookingDetails) : undefined,
            intent.confirmationPending, intent.order ? this.dtoAdapter.adaptOrderDto(intent.order) : undefined)
    }

    private async updateBookingEmailForPayment(venue: Venue, booking: BookingDetails | undefined, emailAddress: string): Promise<void> {
        if (!booking || booking.bookingEmailAddress) {
            return
        }
        await firstValueFrom(this.bookingService.updateBookingEmailAddress(venue.organisationId, venue.businessId, venue.id, booking.bookingId, emailAddress))
        booking.bookingEmailAddress = emailAddress
    }

    pendingDojoSetupIntentId(): string | null {
        return this.windowService.getSessionStorage().getItem(DOJO_SETUP_INTENT_STORAGE_KEY)
    }

    clearPendingDojoSetupIntent(): void {
        this.windowService.getSessionStorage().removeItem(DOJO_SETUP_INTENT_STORAGE_KEY)
    }

    private loadStripe(accountId: string): Promise<Stripe> {
        const existing = this.stripeByAccountId.get(accountId)
        if (existing) {
            return existing
        }
        const promise = loadStripe(environment.stripePublishableKey, {
            stripeAccount: accountId,
        }).then((stripe) => {
            if (stripe === null) {
                throw new Error('Stripe not loaded')
            }
            return stripe
        })
        this.stripeByAccountId.set(accountId, promise)
        return promise
    }
}

const STRIPE_APPEARANCE: Appearance = {
    theme: 'stripe',
    variables: {
        borderRadius: '0.625rem',
        fontFamily: 'Inter',
        fontSizeSm: '1rem',
        colorPrimary: '#158848',
        colorWarning: '#DA3A3E',
        colorWarningText: '#DA3A3E',
        spacingUnit: '4px',
    },
}

const RYFT_THEME = {
    fontFamily: 'Inter, sans-serif',
    baseBackground: '#FFFFFF',
    baseBorderRadius: '10px',
    textColor1: '#212529',
    textColor2: '#6c757d',
}

const DOJO_CHECKOUT_BASE_URL = 'https://pay.dojo.tech/checkout/'
const DOJO_SETUP_INTENT_STORAGE_KEY = 'tablesense.dojo.setupIntentId'

function unsupportedPaymentGateway(gateway: never): never {
    throw new Error(`Unsupported payment gateway: ${gateway}`)
}

function isRyftValidationError(error: unknown): boolean {
    if (!(error instanceof Error) || error.name !== 'RyftError') {
        return false
    }
    const details = (error as Error & { details?: unknown }).details
    return Array.isArray(details) && !error.message.includes('API Error:')
}

class DojoHostedCheckoutSession implements PaymentSession {

    readonly gateway: PaymentGateway = 'dojo'
    readonly walletPaymentAvailable = false
    readonly usesHostedCheckout = true

    constructor(
        private readonly options: PaymentSessionOptions,
        private readonly windowService: WindowService
    ) {}

    setOnValidityChange(_handler: (isValid: boolean) => void): void {
        // The card form and its validation are hosted by Dojo.
    }

    setOnWalletConfirmed(_handler: (intentId: string) => void): void {
        // Setup Intents use Dojo's hosted Card checkout only.
    }

    setOnError(_handler: (message: string) => void): void {
        // Hosted checkout errors are handled on Dojo's page.
    }

    confirm(): Promise<string> {
        this.windowService.getSessionStorage().setItem(
            DOJO_SETUP_INTENT_STORAGE_KEY,
            this.options.intent.id
        )
        this.windowService.replaceLocation(
            `${DOJO_CHECKOUT_BASE_URL}${encodeURIComponent(this.options.intent.id)}`
        )
        // Navigation replaces this document, so the local booking flow must not
        // continue as though card setup had completed in this page.
        return new Promise<string>(() => undefined)
    }

    destroy(): void {
        // No provider UI is mounted in this document.
    }
}

class TableSenseTestSession implements PaymentSession {
    readonly gateway = 'tablesense-test'
    readonly walletPaymentAvailable = false
    private readonly container: HTMLElement
    private readonly card: HTMLInputElement
    private validityHandler: (isValid: boolean) => void = (): void => { }

    constructor(document: Document, private readonly options: PaymentSessionOptions,
        private readonly confirmPayment: (paymentIntentId?: string) => Promise<PaymentIntent>) {
        this.container = document.querySelector<HTMLElement>(options.cardFormSelector)!
        const notice = document.createElement('p')
        notice.textContent = 'TableSense Test payment. No money will be taken. Use 4242 4242 4242 4242 for success or 4000 0000 0000 0002 for a decline.'
        if (options.usage === 'setup-card') {
            notice.textContent += ' For later cancellation charges, use 4000 0000 0000 9995 for insufficient funds or 4000 0000 0000 9979 for delayed success.'
        }
        this.card = document.createElement('input')
        this.card.className = 'form-control'
        this.card.inputMode = 'numeric'
        this.card.autocomplete = 'off'
        this.card.setAttribute('aria-label', 'Test card number')
        this.card.setAttribute('data-testid', 'test-payment-card')
        this.card.value = '4242 4242 4242 4242'
        this.card.oninput = (): void => { this.validityHandler(this.isValid()) }
        this.container.append(notice, this.card)
    }

    private isValid(): boolean {
        const cardNumber = this.card.value.replace(/\s/g, '')
        return ['4242424242424242', '4000000000000002'].includes(cardNumber)
            || this.options.usage === 'setup-card' && ['4000000000000341',
            '4000000000009995', '4000000000000069', '4000000000000127',
            '4000000000000119', '4000000000003220', '4000000000006975',
            '4000000000000036', '4000000000000077', '4000000000009979',
            ].includes(cardNumber)
    }

    setOnValidityChange(handler: (isValid: boolean) => void): void {
        this.validityHandler = handler
        handler(this.isValid())
    }
    setOnWalletConfirmed(_handler: (intentId: string) => void): void { }
    setOnError(_handler: (message: string) => void): void { }

    async confirm(): Promise<string> {
        if (!this.isValid()) throw new PaymentSessionValidationError('Enter one of the TableSense Test card numbers')
        if (this.card.value.replace(/\s/g, '') === '4000000000000002') throw new Error('Test payment declined')
        const result = await this.confirmPayment(this.options.usage === 'setup-card'
            ? `${this.options.intent.id}_card_${this.card.value.replace(/\s/g, '').slice(-4)}` : undefined)
        return result.id
    }

    destroy(): void { this.container.replaceChildren() }
}

class StripeSession implements PaymentSession {

    // Stripe Elements does wallet payments internally, so we don't need to render a separate wallet button or handle wallet callbacks in this session.
    readonly walletPaymentAvailable = false

    private readonly elements: StripeElements
    private validityHandler: (isValid: boolean) => void = () => undefined
    private errorHandler: (message: string) => void = () => undefined

    constructor(
        readonly gateway: 'stripe' | 'breathepay',
        private readonly stripe: Stripe,
        private readonly options: PaymentSessionOptions
    ) {
        this.elements = stripe.elements({
            appearance: STRIPE_APPEARANCE,
            fonts: [
                {
                    cssSrc: 'https://fonts.googleapis.com/css?family=Inter',
                },
            ],
            clientSecret: options.intent.clientSecret,
        })
        const paymentElement = this.elements.create('payment')
        paymentElement.mount(options.cardFormSelector)
        paymentElement.on('change', event => {
            this.validityHandler(event.complete)
        })
    }

    setOnValidityChange(handler: (isValid: boolean) => void): void {
        this.validityHandler = handler
    }

    setOnWalletConfirmed(_handler: (intentId: string) => void): void {
        // Stripe handles wallet payments internally, so we don't need to implement this callback.
    }

    setOnError(handler: (message: string) => void): void {
        this.errorHandler = handler
    }

    async confirm(billingEmailAddress?: string): Promise<string> {
        if (this.options.usage === 'setup-card') {
            const result = await this.stripe.confirmSetup({
                elements: this.elements,
                confirmParams: {
                    payment_method_data: {
                        billing_details: {
                            email: billingEmailAddress,
                        },
                    },
                },
                redirect: 'if_required',
            })
            if (result.error) {
                const message = result.error.message ?? 'Unable to save your card details.'
                if (result.error.type === 'validation_error') {
                    throw new PaymentSessionValidationError(message)
                }
                this.errorHandler(message)
                throw new Error(message)
            }
            if (!result.setupIntent) {
                throw new Error('Stripe setup intent was not returned')
            }
            return result.setupIntent.id
        }
        const result = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                payment_method_data: {
                    billing_details: {
                        email: billingEmailAddress,
                    },
                },
            },
            redirect: 'if_required',
        })
        if (result.error) {
            const message = result.error.message ?? 'Unable to take your payment.'
            if (result.error.type === 'validation_error') {
                throw new PaymentSessionValidationError(message)
            }
            this.errorHandler(message)
            throw new Error(message)
        }
        if (!result.paymentIntent) {
            throw new Error('Stripe payment intent was not returned')
        }
        return result.paymentIntent.id
    }

    destroy(): void {
        // Stripe Elements clean up automatically when their containers are removed.
    }
}

export class RyftSession implements PaymentSession {

    readonly gateway: PaymentGateway = 'ryftpay'
    readonly walletPaymentAvailable: boolean

    private readonly cardForm: RyftCheckoutComponentResponse
    private readonly applePay: RyftApplePayComponentResponse | null
    private readonly googlePay: RyftGooglePayComponentResponse | null
    private readonly cleanups: Array<() => void> = []
    private validityHandler: (isValid: boolean) => void = () => undefined
    private walletHandler: (intentId: string) => void = () => undefined
    private errorHandler: (message: string) => void = () => undefined
    private walletConfirmedIntentId: string | null = null
    private readonly getBillingEmailAddress?: () => string | undefined
    private readonly merchantName: string

    constructor(
        account: PaymentsAccount,
        venue: Venue,
        options: PaymentSessionOptions
    ) {
        const accountId = account.id
        if (!environment.ryftPublicKey) {
            throw new Error('Ryft public key is not configured')
        }
        if (!options.intent.clientSecret || options.intent.clientSecret.trim().length === 0) {
            throw new Error('Ryft client secret is required')
        }
        this.getBillingEmailAddress = options.getBillingEmailAddress
        this.merchantName = options.merchantName ?? venue.displayName
        const usage: RyftUsage = options.usage
        const paymentType: 'Unscheduled' | 'Standard' = options.usage === 'setup-card' || options.saveCardWithPayment
            ? 'Unscheduled' : 'Standard'
        const applePaySelector = account.applePayEnabled
            ? options.applePayWalletButtonSelector
            : undefined

        this.applePay = applePaySelector
            ? this.mountApplePay(accountId, venue, options.intent.clientSecret, usage, paymentType, applePaySelector)
            : null

        this.googlePay = options.googlePayWalletButtonSelector
            ? this.mountGooglePay(accountId, venue, options.intent.clientSecret, usage, paymentType, options.googlePayWalletButtonSelector)
            : null
        this.walletPaymentAvailable = this.applePay !== null || this.googlePay !== null
        this.cardForm = this.mountCardForm(accountId, options.intent.clientSecret, usage, paymentType, options.cardFormSelector)
    }

    setOnValidityChange(handler: (isValid: boolean) => void): void {
        this.validityHandler = handler
    }

    setOnWalletConfirmed(handler: (intentId: string) => void): void {
        this.walletHandler = handler
    }

    setOnError(handler: (message: string) => void): void {
        this.errorHandler = handler
    }

    async confirm(billingEmailAddress?: string): Promise<string> {
        if (this.walletConfirmedIntentId !== null) {
            return this.walletConfirmedIntentId
        }
        if (!this.cardForm) {
            throw new Error('Payment form is not ready. Please refresh the page and try again.')
        }
        const request: { customerEmail?: string } = {}
        if (billingEmailAddress && billingEmailAddress.trim().length > 0) {
            request.customerEmail = billingEmailAddress.trim()
        }
        let result: RyftPaymentAttemptResponse
        try {
            result = await this.cardForm.attemptPayment(
                Object.keys(request).length > 0 ? request : undefined
            )
        } catch (error) {
            if (isRyftValidationError(error)) {
                const message = error instanceof Error
                    ? error.message
                    : 'Please complete your payment details before continuing.'
                throw new PaymentSessionValidationError(message)
            }
            throw error
        }
        if (result.type !== 'final') {
            throw new Error('Additional action is required to complete this payment. Please try again.')
        }
        return this.completeFromFinalResult(result)
    }

    destroy(): void {
        this.cleanups.forEach((cleanup) => {
            try {
                cleanup()
            } catch (error) {
                if (!environment.production) {
                    console.warn('[Ryft] failed to clean up payment session', error)
                }
            }
        })
        this.cleanups.length = 0
    }

    private mountCardForm(
        accountId: string,
        clientSecret: string,
        usage: RyftUsage,
        paymentType: 'Unscheduled' | 'Standard',
        selector: string
    ): RyftCheckoutComponentResponse {
        const controller = createController({
            publicKey: environment.ryftPublicKey,
            clientSecret,
            accountId,
            usage,
            paymentType,
            debugMode: !environment.production,
            theme: RYFT_THEME,
        })
        const cardForm = createCheckout(controller, {
            components: ['field-collection-form', 'card-form'],
            fieldCollectionFormConfig: {
                displayConfig: {
                    validationMessageDisplayMode: 'onSubmit',
                },
                fieldCollection: {
                    customerAddress: {
                        displayMode: 'minimum',
                    },
                },
                text: {
                    postalCodeLabel: 'Postal code',
                },
            },
            cardFormConfig: {
                displayConfig: {
                    fieldLayout: 'separated',
                    validationMessageDisplayMode: 'onSubmit',
                },
            },
        })
        cardForm.mount(selector)
        this.cleanups.push(() => cardForm.unmount())
        cardForm.on('validationChange', (event) => {
            this.validityHandler(event.isValid)
        })
        return cardForm
    }

    private mountApplePay(
        accountId: string,
        venue: Venue,
        clientSecret: string,
        usage: RyftUsage,
        paymentType: 'Unscheduled' | 'Standard',
        selector: string
    ): RyftApplePayComponentResponse {
        const controller = createController({
            publicKey: environment.ryftPublicKey,
            clientSecret,
            accountId,
            usage,
            paymentType,
            debugMode: !environment.production,
            theme: RYFT_THEME,
            beforePaymentAttempt: () => this.beforeWalletPaymentAttempt(),
        })
        const applePay = createApplePay(controller, {
            merchantName: this.merchantName,
            merchantCountryCode: venue.address.getISO3166CountryCode(),
            displayConfig: {
                applePayButtonStyle: 'black',
                applePayButtonType: 'book',
            },
            requiredBillingContactFields: ['postalAddress'],
        })
        applePay.mount(selector)
        this.cleanups.push(() => applePay.unmount())
        applePay.on('paymentSessionResult', (event) => {
            const result = event.result
            if (result.type !== 'final') {
                return
            }
            try {
                const intentId = this.completeFromFinalResult(result as RyftPaymentAttemptFinalResponse)
                this.walletConfirmedIntentId = intentId
                this.walletHandler(intentId)
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error)
                this.errorHandler(message)
            }
        })
        return applePay
    }

    private mountGooglePay(
        accountId: string,
        venue: Venue,
        clientSecret: string,
        usage: RyftUsage,
        paymentType: 'Unscheduled' | 'Standard',
        selector: string
    ): RyftGooglePayComponentResponse {
        const controller = createController({
            publicKey: environment.ryftPublicKey,
            clientSecret,
            accountId,
            usage,
            paymentType,
            debugMode: !environment.production,
            theme: RYFT_THEME,
            beforePaymentAttempt: () => this.beforeWalletPaymentAttempt(),
        })
        const googlePay = createGooglePay(controller, {
            merchantName: this.merchantName,
            merchantCountryCode: venue.address.getISO3166CountryCode(),
            displayConfig: {
                googlePayButtonType: 'plain',
            },
            fieldCollection: {
                emailRequired: true,
                billingAddressRequired: 'full',
                phoneNumberRequired: false,
            },
        })
        googlePay.mount(selector)
        this.cleanups.push(() => googlePay.unmount())
        googlePay.on('paymentSessionResult', (event) => {
            const result = event.result
            if (result.type !== 'final') {
                return
            }
            try {
                const intentId = this.completeFromFinalResult(result as RyftPaymentAttemptFinalResponse)
                this.walletConfirmedIntentId = intentId
                this.walletHandler(intentId)
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error)
                this.errorHandler(message)
            }
        })
        return googlePay
    }

    private beforeWalletPaymentAttempt(): RyftBeforePaymentAttemptResponse {
        if (!this.getBillingEmailAddress) {
            return { type: 'success' }
        }
        const billingEmailAddress = this.getBillingEmailAddress()?.trim()
        if (!billingEmailAddress) {
            return {
                type: 'error',
                data: {
                    walletError: {
                        type: 'billingContactInvalid',
                        message: 'Please enter a valid email address.',
                    },
                },
            }
        }
        return {
            type: 'success',
            data: {
                customerDetails: {
                    email: billingEmailAddress,
                },
            },
        }
    }

    private completeFromFinalResult(result: RyftPaymentAttemptFinalResponse): string {
        const session = result.paymentSession
        if ((session.status === 'Approved' || session.status === 'Captured') && session.id) {
            return session.id
        }
        const message = result.userFacingErrorMessage
            ?? (session.lastError ? `Your payment could not be completed (${session.lastError}).` : null)
            ?? 'Your payment could not be completed. Please check your card details and try again.'
        throw new Error(message)
    }
}
